import React, { Suspense, useContext } from 'react';
import { PlatformContext } from '../context/PlatformContext';

// Import critical components immediately to reduce loading time
import DashboardComponentModern from '../mobile/components/DashboardComponentModern';
import ToolsGridModern from '../mobile/components/ToolsGridModern';
import FullScreenImage from '../components/FullScreenImage';
import ToolsGrid from '../components/ToolsGrid';

const lazy = (factory) => React.lazy(factory);

const componentRegistry = {
  Dashboard: { mobile: DashboardComponentModern, desktop: FullScreenImage },
  ToolsGrid: { mobile: ToolsGridModern, desktop: ToolsGrid },
  StudyGrid: {
    mobile: lazy(() => import('../mobile/components/StudyGridModern')),
    desktop: lazy(() => import('../components/StudyGrid')),
  },
  StudyToolsGrid: {
    mobile: lazy(() => import('../mobile/components/StudyToolsGridModern')),
    desktop: lazy(() => import('../components/StudyToolGrid')),
  },
  RandomTarotCard: {
    mobile: lazy(() => import('../mobile/pages/randomtarotcard')),
    desktop: lazy(() => import('../components/tools/Randomtarotcard')),
  },
  CelticCross: {
    mobile: lazy(() => import('../components/tools/CelticCross')),
    desktop: lazy(() => import('../components/tools/CelticCross')),
  },
  RandomPlayingCard: {
    mobile: lazy(() => import('../mobile/pages/randomplayingcard')),
    desktop: lazy(() => import('../components/tools/Randomplayingcard')),
  },
  RandomCoinFlipper: {
    mobile: lazy(() => import('../mobile/pages/randomcoinflipper')),
    desktop: lazy(() => import('../components/tools/CoinFlipperDesktop')),
  },
  RandomNumber: {
    mobile: lazy(() => import('../mobile/pages/randomnumber')),
    desktop: lazy(() => import('../components/tools/Randomnumber')),
  },
  YesNo: {
    mobile: lazy(() => import('../mobile/pages/yesorno')),
    desktop: lazy(() => import('../components/tools/Yesorno')),
  },
  NumerologyCalculator: {
    mobile: lazy(() => import('../mobile/pages/numerologycalculator')),
    desktop: lazy(() => import('../components/tools/Numerology')),
  },
  NameNumerology: {
    mobile: lazy(() => import('../mobile/pages/numerologyname')),
    desktop: lazy(() => import('../components/tools/Namenumerology')),
  },
  RandomRune: {
    mobile: lazy(() => import('../mobile/pages/randomrune')),
    desktop: lazy(() => import('../components/tools/Randomrune')),
  },
  Profile: {
    mobile: lazy(() => import('../mobile/components/ProfileComponentModern')),
    desktop: lazy(() => import('../components/ProfileDisplayComponent')),
  },
  ProfileEdit: {
    mobile: lazy(() => import('../mobile/components/ProfileComponentModern')),
    desktop: lazy(() => import('../components/ProfileEditComponent')),
  },
  Account: {
    mobile: lazy(() => import('../mobile/components/AccountComponentMobile')),
    desktop: lazy(() => import('../components/ProfileComponent')),
  },
  Settings: {
    mobile: lazy(() => import('../mobile/pages/settings')),
    desktop: lazy(() => import('../components/SettingsDesktop')),
  },
  Messages: {
    mobile: lazy(() => import('../mobile/components/MessagesComponentModern')),
    desktop: lazy(() => import('../components/MessagesDesktop')),
  },
  Academy: {
    mobile: lazy(() => import('../mobile/components/AcademyComponentModern')),
    desktop: lazy(() => import('../components/ProfileComponent')),
  },
  Library: {
    mobile: lazy(() => import('../mobile/components/LibraryComponentModern')),
    desktop: lazy(() => import('../components/ProfileComponent')),
  },
  Progress: {
    mobile: lazy(() => import('../mobile/components/ProgressComponentModern')),
    desktop: lazy(() => import('../components/ProfileComponent')),
  },
  Journal: {
    mobile: lazy(() => import('../mobile/pages/journal.js')),
    desktop: lazy(() => import('../components/JournalDesktop')),
  },
  Login: {
    mobile: lazy(() => import('../mobile/components/LoginMobile')),
    desktop: lazy(() => import('../components/LoginDesktop')),
  },
  Register: {
    mobile: lazy(() => import('../mobile/components/RegisterMobile')),
    desktop: lazy(() => import('../components/LoginDesktop')),
  },
  StudyAstrology: {
    mobile: lazy(() => import('../mobile/pages/studyastrology')),
    desktop: lazy(() => import('../pages/study/astrology')),
  },
  StudyTarot: {
    mobile: lazy(() => import('../mobile/pages/studytarot')),
    desktop: lazy(() => import('../pages/study/tarot')),
  },
  StudyNumerology: {
    mobile: lazy(() => import('../mobile/pages/studynumerology')),
    desktop: lazy(() => import('../pages/study/numerology/numerology')),
  },
  StudyKaballah: {
    mobile: lazy(() => import('../mobile/pages/studykaballah')),
    desktop: lazy(() => import('../pages/study/kaballah/kaballah')),
  },
  StudyRunes: {
    mobile: lazy(() => import('../mobile/pages/studyrunes')),
    desktop: lazy(() => import('../pages/study/runes/runes')),
  },
  DeleteAccount: {
    mobile: lazy(() => import('../mobile/components/DeleteAccountComponentMobile')),
    desktop: lazy(() => import('../components/DeleteAccountComponent')),
  },
  Calendar: {
    mobile: lazy(() => import('../calendar/mobile/CalendarMobilePage.js')),
    desktop: lazy(() => import('../pages/calendar.js')),
  },
};

export const MobileComponents = Object.fromEntries(
  Object.entries(componentRegistry).map(([key, value]) => [key, value.mobile || value.desktop]),
);

export const DesktopComponents = Object.fromEntries(
  Object.entries(componentRegistry).map(([key, value]) => [key, value.desktop || value.mobile]),
);

export const getPlatformComponent = (componentKey, isMobile) => {
  const entry = componentRegistry[componentKey];
  if (!entry) return null;
  return isMobile ? entry.mobile || entry.desktop : entry.desktop || entry.mobile;
};

// Subtle loading component for Suspense
export const LoadingComponent = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '20vh',
    color: 'rgba(255, 215, 0, 0.6)',
    fontSize: '0.9rem',
    opacity: 0.7,
    transition: 'opacity 0.3s ease'
  }}>
    <div style={{
      width: '20px',
      height: '20px',
      border: '2px solid rgba(255, 215, 0, 0.3)',
      borderTop: '2px solid rgba(255, 215, 0, 0.8)',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
  </div>
);

// Minimal loading component for quick transitions
export const MinimalLoadingComponent = () => (
  <div style={{ 
    height: '10vh',
    opacity: 0.3,
    transition: 'opacity 0.2s ease'
  }}></div>
);

// Higher-order component for platform-aware rendering with optimized loading
export const withPlatformAwareness = (ComponentName) => {
  return function PlatformAwareComponent(props) {
    const { platform, loading } = useContext(PlatformContext);
    
    if (loading) {
      return <MinimalLoadingComponent />;
    }

    const isMobile = platform === 'mobileApp' || platform === 'mobileWeb';
    const MobileComponent = MobileComponents[ComponentName];
    const DesktopComponent = DesktopComponents[ComponentName];

    // If component is loaded immediately, render without Suspense
    if (MobileComponent && !MobileComponent.$$typeof) {
      return isMobile ? <MobileComponent {...props} /> : <DesktopComponent {...props} />;
    }

    return (
      <Suspense fallback={<MinimalLoadingComponent />}>
        {isMobile ? <MobileComponent {...props} /> : <DesktopComponent {...props} />}
      </Suspense>
    );
  };
};

// Preload critical components to reduce loading time
export const preloadCriticalComponents = () => {
  // This function is called from App.js but doesn't need to do anything
  // since critical components are already imported immediately
  if (process.env.NODE_ENV === 'development') {
  }
};
